/**
 * React Query 全局 QueryClient
 *
 * 策略分层：
 *   - 4xx 业务错误：页面自己决定怎么提示（表单校验、权限不足等），全局不吭声
 *   - 5xx / 网络错误：页面即便没写 onError，也通过全局 Toast 告知用户，避免静默失败
 *     - 只对用户主动触发的请求提示；后台刷新（refetchOnReconnect 等）静默重试
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { toastBridge } from '@/components/ui/Toast';
import { BusinessError } from './types';


function describeNetworkError(err: unknown): string | null {
  // 5xx：服务器错误
  if (err instanceof BusinessError && err.httpStatus >= 500) {
    return '服务器开小差了，请稍后重试';
  }
  // 非 BusinessError 的原生 Error → 多半是 fetch 网络错误
  if (err instanceof Error && !(err instanceof BusinessError)) {
    // 浏览器明确能感知的断网由 OfflineBanner 负责，这里只处理"连得上但请求失败"
    if (typeof navigator !== 'undefined' && !navigator.onLine) return null;
    return '网络请求失败，请检查连接后重试';
  }
  return null;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      // 后台静默刷新（非首次加载且已有数据）不打扰用户
      if (query.state.data !== undefined) return;
      const msg = describeNetworkError(err);
      if (msg) toastBridge({ message: msg, type: 'error' });
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      // mutation 通常是用户主动动作（提交表单 / 点按钮），无论如何都该给反馈
      const msg = describeNetworkError(err);
      if (msg) toastBridge({ message: msg, type: 'error' });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 秒内不重复请求
      retry: (failureCount, error) => {
        // 4xx 不重试（业务错误），5xx / 网络错误最多重试 1 次
        if (error instanceof BusinessError && error.httpStatus >= 400 && error.httpStatus < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
